import { useEffect, useRef, useState } from 'react';
import { CheckCircle2, Clock, Loader2, Mail, MapPin, Send, ShieldCheck } from 'lucide-react';
import SiteFooter from './SiteFooter';
import PublicHeader from './PublicHeader';
import { SITE } from '@/content/site';
import { useHeadMeta } from '@/lib/head';
import './HomepageFonts.css';
import './Homepage.css';
import './Contact.css';

// Must match TOPICS in deployment/api/contact.mjs.
const TOPICS = ['General question', 'Support', 'Account or billing', 'Privacy or data request', 'Partnership', 'Other'];
const EMPTY = { name: '', email: '', topic: TOPICS[0], subject: '', message: '', website: '' };

function check(form) {
  const errors = {};
  if (!form.name.trim()) errors.name = 'Enter your name.';
  else if (form.name.trim().length > 100) errors.name = 'Use 100 characters or fewer for your name.';
  if (!/^[^\s@<>()[\]\\,;:"]+@[A-Za-z0-9-]+(\.[A-Za-z0-9-]+)+$/.test(form.email.trim())) errors.email = 'Enter a valid email address.';
  if (form.subject.trim().length > 150) errors.subject = 'Use 150 characters or fewer for the subject.';
  if (form.message.trim().length < 10) errors.message = 'Tell us a little more, at least 10 characters.';
  else if (form.message.trim().length > 5000) errors.message = 'Use 5,000 characters or fewer for your message.';
  return errors;
}

function Field({ id, label, required, error, hint, children }) {
  return <div className="ss-contact-field">
    <label htmlFor={id}>{label}{required && <span aria-hidden="true"> *</span>}</label>
    {children}
    {hint && !error && <p className="ss-contact-hint" id={`${id}-hint`}>{hint}</p>}
    {error && <p className="ss-contact-error" id={`${id}-error`}>{error}</p>}
  </div>;
}

export default function ContactPage() {
  useHeadMeta({ title: 'Contact | SEO Signals', description: 'Contact SEO Signals for support, account questions and privacy enquiries. Send a message or email hello@seo-signals.com.', canonical: 'https://seo-signals.com/contact', robots: 'index, follow' });
  const [form, setForm] = useState(EMPTY);
  const [errors, setErrors] = useState({});
  const [state, setState] = useState('idle');
  const [failure, setFailure] = useState('');
  const started = useRef(0);
  useEffect(() => { started.current = Date.now(); }, []);

  const set = key => e => { setForm(f => ({ ...f, [key]: e.target.value })); if (errors[key]) setErrors(x => ({ ...x, [key]: undefined })); };
  const describedBy = (id, hint) => errors[id] ? `${id}-error` : hint ? `${id}-hint` : undefined;

  const submit = async e => {
    e.preventDefault();
    const found = check(form);
    setErrors(found);
    if (Object.keys(found).length) { document.getElementById(Object.keys(found)[0])?.focus(); return; }
    setState('sending');
    setFailure('');
    try {
      const r = await fetch('/api/contact', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ ...form, started_at: started.current }) });
      const body = await r.json().catch(() => ({}));
      if (r.ok) { setState('sent'); return; }
      if (r.status === 400 && body.error?.field) { setErrors({ [body.error.field]: body.error.message }); setState('idle'); return; }
      setFailure(body.error?.message || 'Your message could not be sent.');
      setState('failed');
    } catch {
      setFailure('Your message could not be sent. Check your connection and try again.');
      setState('failed');
    }
  };

  return <div className="ss-home ss-uc ss-contact">
    <PublicHeader />
    <main>
      <section className="ss-contact-hero"><div className="ss-container">
        <p className="ss-eyebrow">GET IN TOUCH</p>
        <h1>Contact SEO Signals</h1>
        <p className="ss-lead">Questions about your account, connecting Google or Bing, or what SEO Signals can do for your site? Send us a message and we’ll reply by email.</p>
      </div></section>
      <section className="ss-container ss-contact-grid">
        <aside className="ss-contact-cards" aria-label="Other ways to reach us">
          <div className="ss-contact-card"><span className="ss-contact-icon ss-tone-blue"><Mail size={20} aria-hidden="true" /></span><div><h2>Email us</h2><p>For support, account and general questions.</p><a href={`mailto:${SITE.contactEmail}`}>{SITE.contactEmail}</a></div></div>
          <div className="ss-contact-card"><span className="ss-contact-icon ss-tone-lime"><Clock size={20} aria-hidden="true" /></span><div><h2>Response time</h2><p>We aim to reply within two business days.</p></div></div>
          <div className="ss-contact-card"><span className="ss-contact-icon ss-tone-amber"><ShieldCheck size={20} aria-hidden="true" /></span><div><h2>Privacy requests</h2><p>Ask to see or delete your data. Write from your account email address so we can verify it’s you. <a href="/privacy">Privacy policy</a></p></div></div>
          <div className="ss-contact-card"><span className="ss-contact-icon ss-tone-ink"><MapPin size={20} aria-hidden="true" /></span><div><h2>Who we are</h2><p>{`SEO Signals is run by ${SITE.operator}, based in ${SITE.locality}, ${SITE.country}.`}</p></div></div>
        </aside>
        <div className="ss-contact-panel">
          {state === 'sent' ? <div className="ss-contact-sent" role="status" data-testid="contact-sent">
            <CheckCircle2 size={36} aria-hidden="true" />
            <h2>Thanks, your message is on its way</h2>
            <p>We’ll reply to {form.email.trim()} as soon as we can. You don’t need to send it again.</p>
            <button type="button" className="ss-button ss-secondary" onClick={() => { setForm(EMPTY); setState('idle'); started.current = Date.now(); }}>Send another message</button>
          </div> : <form onSubmit={submit} noValidate data-testid="contact-form" aria-describedby="contact-required">
            <h2 className="ss-contact-form-title">Send us a message</h2>
            <p className="ss-contact-hint" id="contact-required">Fields marked * are required.</p>
            <div className="ss-contact-row">
              <Field id="name" label="Name" required error={errors.name}><input id="name" name="name" autoComplete="name" value={form.name} onChange={set('name')} placeholder="Your name" aria-invalid={!!errors.name} aria-describedby={describedBy('name')} required /></Field>
              <Field id="email" label="Email" required error={errors.email}><input id="email" name="email" type="email" autoComplete="email" value={form.email} onChange={set('email')} placeholder="you@example.com" aria-invalid={!!errors.email} aria-describedby={describedBy('email')} required /></Field>
            </div>
            <div className="ss-contact-row">
              <Field id="topic" label="Topic"><select id="topic" name="topic" value={form.topic} onChange={set('topic')}>{TOPICS.map(t => <option key={t}>{t}</option>)}</select></Field>
              <Field id="subject" label="Subject" error={errors.subject}><input id="subject" name="subject" value={form.subject} onChange={set('subject')} placeholder="What is this about?" aria-invalid={!!errors.subject} aria-describedby={describedBy('subject')} /></Field>
            </div>
            <Field id="message" label="Message" required error={errors.message} hint="Include the website you’re asking about. Please don’t send passwords, API keys or Google access tokens.">
              <textarea id="message" name="message" rows={7} value={form.message} onChange={set('message')} placeholder="Tell us how we can help" aria-invalid={!!errors.message} aria-describedby={describedBy('message', true)} required />
            </Field>
            {/* Hidden from people; bots that fill it are ignored. */}
            <div className="ss-contact-trap" aria-hidden="true"><label htmlFor="website">Website</label><input id="website" name="website" tabIndex={-1} autoComplete="off" value={form.website} onChange={set('website')} /></div>
            {state === 'failed' && <p className="ss-contact-failed" role="alert" data-testid="contact-failed">{failure} You can also email <a href={`mailto:${SITE.contactEmail}`}>{SITE.contactEmail}</a>.</p>}
            <button type="submit" className="ss-button ss-primary ss-contact-submit" disabled={state === 'sending'} data-testid="contact-submit">
              {state === 'sending' ? <><Loader2 size={18} className="ss-spin" aria-hidden="true" /> Sending…</> : <><Send size={18} aria-hidden="true" /> Send message</>}
            </button>
            <p className="ss-contact-note">We use your details only to reply to you. See our <a href="/privacy">Privacy policy</a>.</p>
          </form>}
        </div>
      </section>
    </main>
    <SiteFooter />
  </div>;
}
